// Plain-language "how the board works" help for the heatmap page — a
// small "?" button in the header opens this modal. Written for
// non-technical users: no audio_mode / trouble_score / relay jargon.

import { useEffect } from 'react'

interface Props {
  open: boolean
  onClose: () => void
}

interface StateEntry {
  swatch: string
  title: string
  text: string
}

const STATES: StateEntry[] = [
  {
    swatch: '#22c55e',
    title: 'Зелёная — в звонке',
    text: 'Телефон сейчас разговаривает. Крупно на карточке — сколько уже длится звонок.',
  },
  {
    swatch: '#ef4444',
    title: 'Красная — проблема',
    text: 'С телефоном что-то не так: ушёл оффлайн, нет SIM-карты, разрядился, слабый сигнал. Иконка в центре показывает что именно, цифра в углу — насколько серьёзно. Чем ярче красный — тем хуже.',
  },
  {
    swatch: '#f59e0b',
    title: 'Жёлтая — звонок завис',
    text: 'Телефон вроде в звонке, но данные с него давно не приходили — связь могла оборваться. Стоит проверить.',
  },
  {
    swatch: '#2c3344',
    title: 'Тёмная — свободен',
    text: 'Телефон на связи, ничего не делает, проблем нет. Намеренно неприметная — чтобы цветные карточки бросались в глаза.',
  },
]

const TIPS: string[] = [
  'Каждый квадрат — один телефон. На доске сразу весь парк.',
  'У каждого телефона постоянное место. Карточка не прыгает: когда телефон начинает звонок или ломается — она просто меняет цвет.',
  'Внизу карточки — уровень сигнала и заряд батареи.',
  'Счётчики сверху: «В звонке» — сколько телефонов сейчас разговаривают, «> 1 мин» — сколько из них дольше минуты.',
  'Кнопка «LIVE» включает автообновление каждые несколько секунд, «Пауза» — останавливает.',
  'Карточка пропадает с доски, только если телефон убрали из системы. Конец звонка или поломка её не убирают.',
]

export function HeatmapHelpModal({ open, onClose }: Props) {
  useEffect(() => {
    if (!open) return
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 md:items-center"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="my-4 flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl md:my-0 dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="heatmap-help-title"
      >
        <div className="flex shrink-0 items-start justify-between border-b border-gray-100 px-6 pt-6 pb-3 dark:border-gray-800">
          <div>
            <h2
              id="heatmap-help-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              Как читать тепловую карту
            </h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Доска показывает все телефоны сразу
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mt-1 -mr-1 p-1 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Закрыть"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="space-y-6 overflow-y-auto px-6 py-5">
          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
              Цвет карточки
            </h3>
            <ul className="space-y-3">
              {STATES.map((s) => (
                <li key={s.title} className="flex items-start gap-3">
                  <span
                    className="mt-0.5 h-4 w-4 shrink-0 rounded"
                    style={{ backgroundColor: s.swatch }}
                    aria-hidden="true"
                  />
                  <p className="flex-1 text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-semibold text-gray-900 dark:text-gray-200">
                      {s.title}.
                    </span>{' '}
                    {s.text}
                  </p>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
              Как пользоваться
            </h3>
            <ul className="space-y-2">
              {TIPS.map((tip) => (
                <li
                  key={tip}
                  className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400"
                >
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                  <span className="flex-1">{tip}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  )
}

/** Round "?" button — sits next to the page title. Sized and coloured
 * to read as an interactive affordance, not a muted helper glyph. */
export function HeatmapHelpButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-7 w-7 items-center justify-center rounded-full text-blue-600 transition-colors hover:bg-blue-100 hover:text-blue-700 dark:text-blue-300 dark:hover:bg-blue-900/40 dark:hover:text-blue-200"
      aria-label="Как работает тепловая карта"
      title="Как работает тепловая карта"
    >
      <svg
        className="h-6 w-6"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    </button>
  )
}
